//# sourceMappingURL=lustre.mjs.map
/// <reference types="./lustre.d.mts" />
import * as $promise from "../../gleam_javascript/gleam/javascript/promise.mjs";
import * as $redraw from "../../redraw/redraw.mjs";
import * as $context from "../../redraw_unsafe/redraw/unsafe/context.mjs";
import { Ok, CustomType as $CustomType, makeError } from "../gleam.mjs";
import * as $effect from "../redraw/lustre/effect.mjs";
import * as $store from "../redraw/store.mjs";

const FILEPATH = "src/redraw/lustre.gleam";

export class Props extends $CustomType {
  constructor(store, children) {
    super();
    this.store = store;
    this.children = children;
  }
}
export const Props$Props = (store, children) => new Props(store, children);
export const Props$isProps = (value) => value instanceof Props;
export const Props$Props$store = (value) => value.store;
export const Props$Props$0 = (value) => value.store;
export const Props$Props$children = (value) => value.children;
export const Props$Props$1 = (value) => value.children;

const context_name = "Redraw.Lustre.Store";

/**
 * Creates a `Store` and initialises the context. Take care to call this at
 * startup, in your `main` function before rendering your application: stores
 * live outside of your React application code.
 */
export function store(initial_state, reducer) {
  let store$1 = $store.new$(initial_state, reducer);
  let $ = $context.create(context_name, undefined);
  
  return store$1;
}

/**
 * Listen to the store at initialisation.
 * Cancels the subscription on cleanup.
 * 
 * @ignore
 */
function use_store_listener(store, set_state) {
  return ((_capture) => { return $redraw.use_effect_(_capture, []); })(
    () => {
      $store.add_listener(store, set_state);
      return () => { return $store.remove_listener(store, set_state); };
    },
  );
}

/**
 * Creates a unique dispatch function that will never get regenerated over time.
 * 
 * @ignore
 */
function use_memo_effect_dispatch(store) {
  return ((_capture) => { return $redraw.use_callback(_capture, []); })(
    (msg) => { return $store.effect_dispatch(store, msg); },
  );
}

/**
 * Creates a unique dispatch function that will never get regenerated over time.
 * 
 * @ignore
 */
function use_memo_dispatch(store) {
  return ((_capture) => { return $redraw.use_callback(_capture, []); })(
    (msg) => { return $store.dispatch(store, msg); },
  );
}

/**
 * Stores the current `Store` value, lazily initialised. Every time the
 * store is updated, the state gets updated too.
 * 
 * @ignore
 */
function use_store_state(store) {
  return $redraw.use_lazy_state(() => { return $store.current_state(store); });
}

/**
 * Creates a state for the current `Store` state, generates the appropriate
 * `dispatch` function, and listens to `Store` updates. Ensures states will
 * never be generated twice with a `memo`.
 * 
 * @ignore
 */
function use_store_subscription(store) {
  let $ = use_store_state(store);
  let state = $[0];
  let set_state = $[1];
  let dispatch = use_memo_dispatch(store);
  let effect_dispatch = use_memo_effect_dispatch(store);
  use_store_listener(store, set_state);
  return ((_capture) => { return $redraw.use_memo(_capture, [state]); })(
    () => { return [state, dispatch, effect_dispatch]; },
  );
}

/**
 * Creates the provider for the `Store`. Every Redraw Lustre application
 * should includes a `Store` creation, and injects the provider in the
 * React tree.
 */
export function provider() {
  let name = "Redraw.Lustre(Provider)";
  return $redraw.component_(
    name,
    (props) => {
      let ctx = use_store_subscription(props.store);
      let $ = $redraw.use_ref_(store);
      
      let $1 = $context.get(context_name);
      let context;
      if ($1 instanceof Ok) {
        context = $1[0];
      } else {
        throw makeError(
          "let_assert",
          FILEPATH,
          "redraw/lustre",
          29,
          "provider",
          "Pattern match failed, no pattern matched the value.",
          {
            value: $1,
            start: 1039,
            end: 1089,
            pattern_start: 1050,
            pattern_end: 1061
          }
        )
      }
      return $redraw.provider(context, ctx, props.children);
    },
  );
}

/**
 * Get the current state from the context store.
 * Fails if `Store` provider is not in the React tree.
 */
export function use_state() {
  let $ = $context.get(context_name);
  let context;
  if ($ instanceof Ok) {
    context = $[0];
  } else {
    throw makeError(
      "let_assert",
      FILEPATH,
      "redraw/lustre",
      40,
      "use_state",
      "Pattern match failed, no pattern matched the value.",
      {
        value: $,
        start: 1358,
        end: 1408,
        pattern_start: 1369,
        pattern_end: 1380
      }
    )
  }
  let $1 = $redraw.use_context(context);
  let state = $1[0];
  return state;
}

/**
 * Get the `dispatch` function from the context store.
 * Fails if `Store` provider is not in the React tree.
 */
export function use_dispatch() {
  let $ = $context.get(context_name);
  let context;
  if ($ instanceof Ok) {
    context = $[0];
  } else {
    throw makeError(
      "let_assert",
      FILEPATH,
      "redraw/lustre",
      48,
      "use_dispatch",
      "Pattern match failed, no pattern matched the value.",
      {
        value: $,
        start: 1659,
        end: 1709,
        pattern_start: 1670,
        pattern_end: 1681
      }
    )
  }
  let $1 = $redraw.use_context(context);
  let dispatch = $1[1];
  return dispatch;
}

export function use_effect_dispatch() {
  let $ = $context.get(context_name);
  let context;
  if ($ instanceof Ok) {
    context = $[0];
  } else {
    throw makeError(
      "let_assert",
      FILEPATH,
      "redraw/lustre",
      54,
      "use_effect_dispatch",
      "Pattern match failed, no pattern matched the value.",
      {
        value: $,
        start: 1873,
        end: 1923,
        pattern_start: 1884,
        pattern_end: 1895
      }
    )
  }
  let $1 = $redraw.use_context(context);
  let effect_dispatch = $1[2];
  return effect_dispatch;
}

/**
 * Derives data from the current state from the context store.
 * Fails if `Store` provider is not in the React tree.
 */
export function use_selector(selector) {
  let $ = $context.get(context_name);
  let context;
  if ($ instanceof Ok) {
    context = $[0];
  } else {
    throw makeError(
      "let_assert",
      FILEPATH,
      "redraw/lustre",
      62,
      "use_selector",
      "Pattern match failed, no pattern matched the value.",
      {
        value: $,
        start: 2210,
        end: 2260,
        pattern_start: 2221,
        pattern_end: 2232
      }
    )
  }
  let $1 = $redraw.use_context(context);
  let state = $1[0];
  return selector(state);
}
